// libs
import React, { memo, useEffect, useRef, useMemo } from 'react';

// Analytics
import { onProductClicked, onSearchResultsViewed } from 'shared/components/ImageSearch/analytics';

// styles
import {
  Container,
  ProductGrid,
  Title,
  ProductCard,
  ProductImage,
  ProductInfo,
  ProductTitle,
  ProductSubtitle,
  PriceContainer,
  OriginalPrice,
  DiscountedPrice,
  DiscountPercent,
  LoadingIndicator,
} from './styles';

interface Product {
  id: string;
  actionUrl: string;
  imageUrl: string;
  title: string;
  subTitle: string;
  discountedPrice?: number;
  price: number;
  discount?: number;
}

interface SimilarProductsProps {
  data: Product[];
  loadMore: () => void;
  loading: boolean;
}

const SimilarProducts = ({ data, loadMore, loading }: SimilarProductsProps) => {
  const products = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const loaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const loaderElem = loaderRef.current;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderElem) {
      observer.observe(loaderElem);
    }

    return () => {
      if (loaderElem) {
        observer.unobserve(loaderElem);
      }
    };
  }, [loadMore, loading]);

  const renderProduct = (item: Product, index: number) => {
    const handleProductClick = () => {
      onProductClicked(item.id, index + 1);
    };

    return (
      <ProductCard key={item.id} href={item.actionUrl} onClick={handleProductClick}>
        <ProductImage src={item.imageUrl} alt={item.title} />
        <ProductInfo>
          <ProductTitle>{item.title}</ProductTitle>
          <ProductSubtitle>{item.subTitle}</ProductSubtitle>
          <PriceContainer>
            {item.discountedPrice ? (
              <>
                <DiscountedPrice>₹{item.discountedPrice}</DiscountedPrice>
                <OriginalPrice>₹{item.price}</OriginalPrice>
                {item.discount && <DiscountPercent>{item.discount}% Off</DiscountPercent>}
              </>
            ) : (
              <DiscountedPrice>₹{item.price}</DiscountedPrice>
            )}
          </PriceContainer>
        </ProductInfo>
      </ProductCard>
    );
  };

  // Track search results viewed
  useEffect(() => {
    if (products.length > 0) {
      onSearchResultsViewed(products.length);
    }
  }, [products.length]);

  if (products.length === 0) return null;

  return (
    <Container>
      <Title>Similar Products</Title>
      <ProductGrid>{products.map((product, index) => renderProduct(product, index))}</ProductGrid>
      <div ref={loaderRef}>
        {loading && <LoadingIndicator>Loading more products...</LoadingIndicator>}
      </div>
    </Container>
  );
};

export default memo(SimilarProducts);
